import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Layers, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProBadge } from '@/components/ProBadge';
import type { AddableLayerType } from '@/lib/layers/layerTypeConfig';
import { LAYER_MENU_ITEMS } from './layerMenuItems';

interface Props {
  onAdd: (type: AddableLayerType) => void;
  disabled?: boolean;
  /** Show the "Add layer" text next to the icon (side panel); false = icon-only (timeline toolbar). */
  showLabel?: boolean;
  triggerTestId?: string;
}

export const AddLayerMenu: React.FC<Props> = ({
  onAdd, disabled, showLabel = true, triggerTestId = 'add-layer',
}) => {
  const { t } = useTranslation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid={triggerTestId}
          size="sm"
          variant="default"
          disabled={disabled}
          title={t('projectEditor.layers.add')}
          aria-label={t('projectEditor.layers.add')}
          className={cn(showLabel ? undefined : 'h-8 w-8 p-0')}
        >
          {disabled ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />}
          {showLabel && <span className="ml-1">{t('projectEditor.layers.add')}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="flex items-center justify-between gap-3 py-1 text-xs font-normal text-muted-foreground">
          {t('projectEditor.layers.add')}
          <ProBadge />
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LAYER_MENU_ITEMS.map(({ type, Icon, labelKey }) => (
          <DropdownMenuItem
            key={type}
            data-testid={`add-layer-${type}`}
            onClick={() => onAdd(type)}
          >
            <Icon className="mr-2 h-4 w-4" />
            {t(labelKey)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AddLayerMenu;
